/* ============================================================
   FitLibrary — fitlibrary-pcar.js
   Product collection carousel controller.

   One controller per [data-pcar] element. Nothing is hardcoded:
   the controller reads whatever product cards the Shopify section
   actually rendered, so adding, removing or reordering blocks in
   the theme editor needs no change here. Two instances on the same
   page are fully independent — every handler is bound to elements
   inside its own instance, and the only document-level listeners
   are the four Shopify design-mode events, bound exactly once.

   Native scroll-snap does the swiping. There are no drag handlers
   and no pointer capture, so links, CTAs and hover states inside
   the cards behave exactly as they did in the grid.

   2026-09-02 · PAGE INDICATOR. The controller now also builds the
   Soft Glass Dot row: one dot per PAGE (two cards per page on
   desktop, one on mobile), rebuilt whenever the measured page count
   changes, hidden entirely when everything already fits.

   WHY THE ROW IS INSERTED AFTER `.pcar` AND NOT INSIDE IT. The
   arrows are centred with `top: 50%` of `.pcar`, which only lands
   on the card centre because the track's top and bottom bleed are
   equal. Appending the dots inside `.pcar` would add height at the
   bottom and drag both arrows below the cards. Inserting the row as
   the NEXT SIBLING leaves `.pcar` exactly the height it was.

   It is still built rather than authored in Liquid so that neither
   section's markup, block loop or reveal group had to change: the
   track's only element children must stay the cards, or the
   nth-child reveal stagger in fitlibrary-motion.css breaks.
   ============================================================ */
(function () {
  "use strict";

  if (window.__flPcarBound) return;
  window.__flPcarBound = true;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  function elementChildren(node) {
    var out = [];
    var kids = node.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 1) out.push(kids[i]);
    }
    return out;
  }

  function init(root) {
    if (!root || root.getAttribute("data-pcar-ready") === "1") return;

    var track = root.querySelector("[data-pcar-track]");
    if (!track) return;

    var prev = root.querySelector("[data-pcar-prev]");
    var next = root.querySelector("[data-pcar-next]");
    var label = root.querySelector("[data-pcar-page]");

    var dotsWrap = null;
    var dotBtns = [];

    root.setAttribute("data-pcar-ready", "1");

    function cards() {
      return elementChildren(track);
    }

    function perView() {
      var list = cards();
      if (!list.length) return 1;
      var w = list[0].getBoundingClientRect().width;
      if (!w) return 1;
      var cs = window.getComputedStyle(track);
      var gap = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
      var n = Math.round((track.clientWidth + gap) / (w + gap));
      if (!isFinite(n) || n < 1) n = 1;
      return Math.min(n, list.length || 1);
    }

    function pageCount(list, per) {
      return Math.max(1, Math.ceil(list.length / per));
    }

    function step(list, per) {
      if (list.length <= per) return 0;
      return list[per].offsetLeft - list[0].offsetLeft;
    }

    function currentPage(list, per) {
      var s = step(list, per);
      if (s <= 0) return 0;
      var page = Math.round(track.scrollLeft / s);
      return Math.min(pageCount(list, per) - 1, Math.max(0, page));
    }

    function goToCard(index) {
      var list = cards();
      if (!list.length) return;
      var i = Math.min(list.length - 1, Math.max(0, index));
      track.scrollTo({
        left: list[i].offsetLeft - list[0].offsetLeft,
        behavior: prefersReducedMotion() ? "auto" : "smooth"
      });
    }

    function goToPage(page) {
      goToCard(page * perView());
    }

    function onDotClick(e) {
      e.preventDefault();
      var raw = e.currentTarget.getAttribute("data-pcar-goto");
      var i = parseInt(raw, 10);
      if (!isFinite(i)) return;
      goToPage(i);
    }

    /* Creates the row on first need and rebuilds the beads only when the
       measured page count actually changes, so a resize that does not
       cross a breakpoint costs nothing. */
    function buildDots(total, paged) {
      if (!paged || total < 2) {
        if (dotsWrap) dotsWrap.hidden = true;
        return;
      }

      if (!dotsWrap) {
        if (!root.parentNode) return;
        dotsWrap = document.createElement("div");
        dotsWrap.className = "pcar__dots";
        dotsWrap.setAttribute("data-pcar-dots", "");
        dotsWrap.setAttribute("role", "group");
        var trackLabel = track.getAttribute("aria-label");
        dotsWrap.setAttribute("aria-label", trackLabel ? trackLabel + " pages" : "Carousel pages");
        root.parentNode.insertBefore(dotsWrap, root.nextSibling);
        root.__flPcarDots = dotsWrap;
      }

      dotsWrap.hidden = false;

      if (dotBtns.length !== total) {
        while (dotsWrap.firstChild) dotsWrap.removeChild(dotsWrap.firstChild);
        dotBtns = [];
        for (var i = 0; i < total; i++) {
          var b = document.createElement("button");
          b.type = "button";
          b.className = "pcar-dot";
          b.setAttribute("data-pcar-goto", String(i));
          b.setAttribute("aria-label", "Go to page " + (i + 1) + " of " + total);
          b.addEventListener("click", onDotClick);
          dotsWrap.appendChild(b);
          dotBtns.push(b);
        }
      }
    }

    function sync() {
      var list = cards();
      var per = perView();
      var total = pageCount(list, per);
      var page = currentPage(list, per);
      var paged = list.length > per;

      if (prev) {
        prev.hidden = !paged;
        prev.disabled = page <= 0;
      }
      if (next) {
        next.hidden = !paged;
        next.disabled = page >= total - 1;
      }
      if (label) {
        label.textContent = paged ? "Showing page " + (page + 1) + " of " + total : "";
      }

      buildDots(total, paged);
      for (var i = 0; i < dotBtns.length; i++) {
        var on = i === page;
        dotBtns[i].setAttribute("aria-current", on ? "true" : "false");
        if (on) {
          dotBtns[i].className = "pcar-dot is-active";
        } else {
          dotBtns[i].className = "pcar-dot";
        }
      }
    }

    if (prev) {
      prev.addEventListener("click", function (e) {
        e.preventDefault();
        var list = cards();
        var per = perView();
        goToPage(currentPage(list, per) - 1);
      });
    }

    if (next) {
      next.addEventListener("click", function (e) {
        e.preventDefault();
        var list = cards();
        var per = perView();
        goToPage(currentPage(list, per) + 1);
      });
    }

    track.addEventListener("keydown", function (e) {
      if (e.target !== track) return;
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      e.preventDefault();
      var list = cards();
      var per = perView();
      goToPage(currentPage(list, per) + (e.key === "ArrowRight" ? 1 : -1));
    });

    var frame = 0;
    track.addEventListener("scroll", function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = 0;
        sync();
      });
    }, { passive: true });

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { sync(); });
      ro.observe(track);
      root.__flPcarObserver = ro;
    } else {
      root.__flPcarResize = function () { sync(); };
      window.addEventListener("resize", root.__flPcarResize);
    }

    root.__flPcarSync = sync;
    root.__flPcarReveal = goToCard;

    sync();
  }

  function destroy(root) {
    if (!root) return;
    if (root.__flPcarObserver) {
      root.__flPcarObserver.disconnect();
      root.__flPcarObserver = null;
    }
    if (root.__flPcarResize) {
      window.removeEventListener("resize", root.__flPcarResize);
      root.__flPcarResize = null;
    }
    if (root.__flPcarDots && root.__flPcarDots.parentNode) {
      root.__flPcarDots.parentNode.removeChild(root.__flPcarDots);
    }
    root.__flPcarDots = null;
    root.removeAttribute("data-pcar-ready");
  }

  function initAll(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    var found = host.querySelectorAll("[data-pcar]");
    for (var i = 0; i < found.length; i++) init(found[i]);
    if (host !== document && host.matches && host.matches("[data-pcar]")) init(host);
  }

  function destroyAll(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    var found = host.querySelectorAll("[data-pcar]");
    for (var i = 0; i < found.length; i++) destroy(found[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Shopify theme editor. A section re-render replaces the markup, so the new
     instance is initialised and the old observer is released. Selecting a card
     block brings that card into the viewport instead of leaving the merchant
     editing something they cannot see. */
  document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });
  document.addEventListener("shopify:section:unload", function (e) { destroyAll(e.target); });

  document.addEventListener("shopify:block:select", function (e) {
    var node = e.target;
    if (!node || !node.closest) return;
    var root = node.closest("[data-pcar]");
    if (!root || typeof root.__flPcarReveal !== "function") return;
    var track = root.querySelector("[data-pcar-track]");
    if (!track) return;
    var card = node;
    while (card && card.parentElement !== track) card = card.parentElement;
    if (!card) return;
    var list = elementChildren(track);
    var index = list.indexOf(card);
    if (index < 0) return;
    root.__flPcarReveal(index);
  });

  document.addEventListener("shopify:section:select", function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector("[data-pcar]") : null;
    if (root && typeof root.__flPcarSync === "function") root.__flPcarSync();
  });
})();